import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
} from '@mui/material';
import { backupService } from '../services/backup.service';
import { statsService, type StatsData } from '../services/stats.service';
import type { BackupLog } from '../types/backup.types';

// F-05: the stats shape lived here as a third private copy next to
// statsService.StatsData (backupService.getStats / BackupStats were deleted in X-4). statsService is the
// one that matches backend internal/stats OverviewStats field for field.

export default function Overview() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [recentLogs, setRecentLogs] = useState<BackupLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, logsPage] = await Promise.all([
        statsService.getStats(),
        backupService.getLogs({ limit: 5 }),
      ]);

      setStats(statsData);
      setRecentLogs(logsPage.items.slice(0, 5));
    } catch (error) {
      console.error('Failed to load overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success':
        return '成功';
      case 'failed':
        return '失敗';
      case 'pending':
        return '等待中';
      case 'running':
        return '執行中';
      case 'cancelled':
        return '已取消';
      default:
        return status;
    }
  };

  // 格式化檔案大小
  const formatSize = (bytes: number | undefined | null): string => {
    if (bytes === undefined || bytes === null || isNaN(bytes) || bytes === 0) {
      return '0 B';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
      return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    }
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  // 格式化時間
  const formatTime = (timeStr: string | undefined | null): string => {
    if (!timeStr) return '-';
    try {
      const date = new Date(timeStr);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return timeStr;
    }
  };

  // 格式化檔案數量
  const formatFileCount = (total: number | undefined): string => {
    if (total === undefined || total === null || total === 0) {
      return '-';
    }
    return `${total} 個檔案`;
  };

  // 計算成功率。沒有任何備份任務時顯示「—」而非 100%（QA #21：零任務的
  // 成功率沒有意義，硬回 100% 會誤導）。
  const calculateSuccessRate = (): string => {
    if (!stats) return '—';
    const total = stats.success_backups + stats.failed_backups;
    if (total === 0) return '—';
    const rate = (stats.success_backups / total) * 100;
    return `${rate.toFixed(0)}%`;
  };

  // 骨架畫面
  const renderSkeleton = () => (
    <Box>
      <Typography variant="h4" gutterBottom>
        系統總覽
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        備份系統狀態一覽
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2, mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: 3 }}>
              <Skeleton width={100} height={20} />
              <Skeleton width={80} height={48} sx={{ mt: 1 }} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Skeleton width={150} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Paper>
    </Box>
  );

  if (loading) {
    return renderSkeleton();
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        系統總覽
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        備份系統狀態一覽
      </Typography>

      {/* 統計卡片 */}
      <Grid container spacing={3} sx={{ mt: 2, mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, borderLeft: '4px solid #667eea' }}>
            <Typography variant="body2" color="text.secondary">
              總備份檔案數
            </Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>
              {stats?.total_files || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, borderLeft: '4px solid #764ba2' }}>
            <Typography variant="body2" color="text.secondary">
              總儲存空間使用
            </Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>
              {stats?.total_size_formatted || formatSize(stats?.total_size_bytes)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, borderLeft: '4px solid #27ae60' }}>
            <Typography variant="body2" color="text.secondary">
              備份成功率
            </Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>
              {calculateSuccessRate()}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, borderLeft: '4px solid #f39c12' }}>
            <Typography variant="body2" color="text.secondary">
              成功 / 失敗
            </Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>
              <Box component="span" sx={{ color: 'success.main' }}>
                {stats?.success_backups || 0}
              </Box>
              <Box component="span" sx={{ mx: 1, color: 'text.secondary' }}>
                /
              </Box>
              <Box component="span" sx={{ color: 'error.main' }}>
                {stats?.failed_backups || 0}
              </Box>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 最近備份記錄 */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          最近備份記錄
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>狀態</TableCell>
                <TableCell>備份時間</TableCell>
                <TableCell>檔案數量</TableCell>
                <TableCell>總大小</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Chip
                      label={getStatusText(log.status)}
                      color={getStatusColor(log.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatTime(log.started_at || log.created_at)}</TableCell>
                  <TableCell>{formatFileCount(log.total_files)}</TableCell>
                  <TableCell>{formatSize(log.total_size_bytes)}</TableCell>
                </TableRow>
              ))}
              {recentLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    暫無備份記錄
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}